import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';

import { ApiParams } from '../core-api.service';
import { Operator } from './enums/operator.enum';
import { Paginated } from './models/paginated.model';
import { SearchField } from './models/search/search-field.model';

type BasicValueType = string | number | boolean

export type FilterCondition<V = BasicValueType | BasicValueType[]> = {
  key?: string,
  keys?: string[],
  operator: string | Operator,
  value: V,
  // Optional metadata fields for storing additional context
  meta?: {
    type?: string,
    label?: string,
    [key: string]: unknown
  }
}

export type EvaluationOptions = {
  caseInsensitive?: boolean
  regexFlags?: string
}

export function splitMultiFilterString(str: string): string[] {
  return str.split(/,(?![^[]*\])/);
}

export function splitFilterString(str: string): string[] {
  const split = str.split(/(!=|=~|!~|<=|>=|>|<|=)/);
  if (split.length === 3) {
    return split;
  } else if (split.length > 3) {
    return [...split.slice(0, 2), split[2].split(',')[0]];
  } else {
    throw new Error(`Found invalid filter string: '${str}'.`);
  }
}

/**
 * Returns an array of items broken out of a multi-filter string which supports in operators, `=|` or `!=|`.
 * @param {string} multiFilterString - String representing mulitple filters, i.e.`'abc=|[xyz,uvw],def!=rst'`
 * @param onInvalidFilter
 * @returns {string[]} - List of filters, i.e. `['abc=xyz','abc=uvw','def!=rst']`
 */
export function decompressInOperatorsToArray(multiFilterString: string, onInvalidFilter?: (filter: string) => void ): string[] {
  return flatten(splitMultiFilterString(multiFilterString).map((filter) => {
    if (/^.+=\|\[.+\]$/.test(filter)) {
      const split = filter.split(/(!=\||=\|)/);
      return split[2]
        .substr(1, split[2].length -2)
        .split(',')
        .map((v) => `${split[0]}${split[1].replace('|', '')}${v}`);
    } else if (!/^.+(!=|=~|!~|<=|>=|>|<|=).+$/.test(filter)) {
      if (onInvalidFilter) {
        onInvalidFilter(filter);
      }
      return null;
    }
    return filter;
  }).filter((filter) => Boolean(filter)));
}

/**
 * Returns a string representing multiple filters that support in operators, `=|` or `!=|`.
 * @param {Map<string, SearchField>} cache - Object of SearchField keyed by field name
 * @param {string[]} filters - List of filters, i.e. `['abc=xyz','abc=uvw','def!=rst']`
 * @returns {string} - String representing mulitple filters, i.e.`'abc=|[xyz,uvw],def!=rst'`
 */
export function compressInOperatorsToString(cache: Map<string, SearchField>, filters: string[]): string {
  const fields: { [key: string]: { meta: string[], value: string[] } } = {};
  for (const filter of filters) {
    const split = splitFilterString(filter);
    const meta = [cache.get(split[0])?.type || 'string', split[0], split[1]];
    const key = meta.join('');
    if (fields[key]) {
      fields[key].value.push(split[2]);
    } else {
      fields[key] = { meta, value: [split[2]] };
    }
  }
  return Object.values(fields).map(({ meta, value }) => {
    const [fieldType, fieldName, operator] = meta;
    return fieldType === 'string' && /^(=|!=)$/.test(operator) && value.length > 1
      ? `${fieldName}${operator}|[${value.join(',')}]`
      : value.length > 1
        ? value.map((v) => `${fieldName}${operator}${v}`).join(',')
        : `${fieldName}${operator}${value}`;
  }).join(',');
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function hasMatch(values: any[], compareFn: (any) => boolean): boolean {
  return values.map((value) => compareFn(value)).some((v) => v === true);
}

export function evaluateFilterCondition<T>(item: T, condition: FilterCondition, options?: EvaluationOptions): boolean {
  let values = [];
  if (condition.key) {
    values.push(get(item, condition.key));
  }
  if (condition.keys?.length > 0) {
    values = Array.from(new Set(values.concat(condition.keys.map((key) => get(item, key)))));
  }
  let compareValue = condition.value;
  const evaluateAsInOperator = Array.isArray(compareValue);
  if (evaluateAsInOperator && ![Operator.EQ.valueOf(), Operator.NEQ.valueOf()].includes(condition.operator)) {
    throw new Error(`Invalid FilterCondition provided. In operation is only supported for 'equal to' and 'not equal to' operators: ${JSON.stringify(condition)}`);
  }
  const valueType = Array.from(new Set(values.map((v) => typeof v)));
  const compareValueType = Array.from(new Set(((evaluateAsInOperator ? compareValue : [compareValue]) as BasicValueType[]).map((v) => typeof v)));
  if (valueType.length > 1 || compareValueType.length > 1 || valueType[0] !== compareValueType[0]) {
    throw new Error(`Invalid FilterCondition provided. Cannot compare mixed types: ${JSON.stringify(condition)}`);
  }
  if (options?.caseInsensitive && valueType[0] === 'string') {
    values = values.map((value) => value.toLowerCase());
    compareValue = evaluateAsInOperator
      ? (compareValue as BasicValueType[]).map((v) => String(v).toLowerCase())
      : String(compareValue).toLowerCase();
  }
  switch (condition.operator) {
    case Operator.LT.valueOf():
      return hasMatch(values, (value) => value < compareValue);
    case Operator.LTE.valueOf():
      return values.map((value) => value <= compareValue).some((v) => v === true);
    case Operator.EQ.valueOf():
      return values.map((value) => evaluateAsInOperator ? (compareValue as BasicValueType[]).includes(value) : value === compareValue).some((v) => v === true);
    case Operator.NEQ.valueOf():
      return values.map((value) => evaluateAsInOperator ? !(compareValue as BasicValueType[]).includes(value) : value !== compareValue).some((v) => v === true);
    case Operator.GTE.valueOf():
      return values.map((value) => value >= compareValue).some((v) => v === true);
    case Operator.GT.valueOf():
      return values.map((value) => value > compareValue).some((v) => v === true);
    case Operator.REG.valueOf():
      return values.map((value) => RegExp(
          String(compareValue),
          Array.from(new Set((options?.regexFlags || '') + (options?.caseInsensitive ? 'i' : ''))).join(''),
        ).test(value)
      ).some((v) => v === true);
    default:
      return false;
  }
}

/**
 * Converts 'group=|[abc,def]' into FilterCondition[], i.e. { key: 'group', operator: '=', value: ['abc', 'def'] }
 */
export function filterStringToFilterConditionArray(filterString: string): FilterCondition[] {
  const filterMap = {};
  const filtersSplit = decompressInOperatorsToArray(filterString || '');
  for (const filter of filtersSplit) {
    let key, operator, value;
    try {
      [key, operator, value] = splitFilterString(filter);
    } catch (error) {
      this.logger.error(`Invalid filter: ${filter}`)
    }
    const keyop = `${key}:${operator}`;
    // consolidate filters with same key and operator (for 'in' and 'not in')
    if (isNil(filterMap[keyop])) {
      filterMap[keyop] = value;
    } else if (Array.isArray(filterMap[keyop])) {
      filterMap[keyop].push(value);
    } else {
      filterMap[keyop] = [filterMap[keyop], value];
    }
  }
  const filters = [];
  for (const [keyop, value] of Object.entries(filterMap)) {
    const [key, operator] = keyop.split(':');
    const keys = Array.from(new Set(key.replace(/(^\[)|(\]$)/g, '').split(',')));
    filters.push(
      keys.length === 1
        ? { key: keys[0], operator, value }
        : { keys, operator, value }
    );
  }
  return filters;
}

/**
 * Converts FilterCondition[], i.e. { key: 'group', operator: '=', value: ['abc', 'def'] } into 'group=|[abc,def]'
 */
export function filterConditionArrayToFilterString(filterConditions: FilterCondition[]): string {
  const filterStrings = [];
  for (const condition of filterConditions) {
    if (!condition?.value) {
      continue;
    }
    const key = Array.isArray(condition.keys) ? `[${condition.keys}]` : condition.key;
    const operator = ['!=', '='].includes(condition.operator) && (Array.isArray(condition.value) && condition.value.length > 1)
      ? `${condition.operator}|`
      : condition.operator;
    const value = Array.isArray(condition.value)
      ? condition.value.length > 1
        ? `[${condition.value}]`
        : condition.value[0]
      : condition.value;
    if (key && value) {
      filterStrings.push(`${key}${operator}${value}`);
    }
  }
  return filterStrings.join(',');
}

export function queryResource<T>(items: T[], params?: ApiParams): Paginated<T[]> {
  let results = items || [];
  // apply filters
  if (params?.filter) {
    const filterString = Array.isArray(params.filter) ? (params.filter as string[]).join(',') : params.filter;
    for (const condition of filterStringToFilterConditionArray(filterString as string)) {
      results = results.filter((item) => evaluateFilterCondition<T>(item, condition, { caseInsensitive: true }));
    }
  }
  const total = results.length;
  // apply sort
  if (params?.sort) {
    const sortSplit = params.sort.split(',') || [];
    const fields = [];
    const directions = [];
    for (const sort of sortSplit) {
      const split = sort.split(':');
      fields.push(split[0]);
      directions.push(split[1]);
    }
    results = orderBy(results, fields, directions);
  }
  // apply limit/after_id
  let count;
  let index = 0;
  if (params?.limit > 0) {
    if (params.after_id) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      index = results.findIndex((item) => (item as any)._id === params?.after_id) + 1;
    } else if (params.page) {
      index = params.page * params.limit;
    }
    count = Math.min(index + params.limit, results.length);
    results = results.slice(index, count);
  }
  return { count: count || results.length, total, results };
}

